import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Divider, Paper, Chip, Link,
  List, ListItem, ListItemText, Alert,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import imgNavMenu from './images/nav-menu.png';
import imgPodManagement from './images/pod-management.png';
import imgPodForm from './images/pod-form.png';
import imgEpicPlanner from './images/epic-planner.png';
import imgEpicForm from './images/epic-form.png';
import imgWorkstreamForecaster from './images/workstream-forecaster.png';
import imgBacklog from './images/backlog.png';
import imgBacklogExpanded from './images/backlog-expanded.png';

interface Section {
  id: string;
  label: string;
  subSections?: { id: string; label: string }[];
}

const sections: Section[] = [
  { id: 'overview', label: 'Overview' },
  {
    id: 'getting-started', label: 'Getting Started',
    subSections: [
      { id: 'gs-navigation', label: 'Navigating the App' },
      { id: 'gs-demo', label: 'Loading Demo Data' },
    ],
  },
  {
    id: 'pod-management', label: 'Pod Management',
    subSections: [
      { id: 'pm-create', label: 'Creating a Pod' },
      { id: 'pm-velocity', label: 'Understanding Velocity' },
      { id: 'pm-members', label: 'Adding Team Members' },
      { id: 'pm-drag', label: 'Drag & Drop' },
    ],
  },
  {
    id: 'epic-planner', label: 'Epic Planner',
    subSections: [
      { id: 'ep-create', label: 'Creating an Epic' },
      { id: 'ep-categories', label: 'Categories' },
      { id: 'ep-stories', label: 'Stories & Story Points' },
      { id: 'ep-overrides', label: 'Manual Overrides' },
    ],
  },
  {
    id: 'workstream-forecaster', label: 'Workstream Forecaster',
    subSections: [
      { id: 'wf-timeline', label: 'Timeline & Sprints' },
      { id: 'wf-burndown', label: 'Epic Burn Visualization' },
      { id: 'wf-overrides', label: 'Sprint Overrides' },
      { id: 'wf-attention', label: 'Attention Warning' },
    ],
  },
  {
    id: 'backlog', label: 'Backlog',
    subSections: [
      { id: 'bl-table', label: 'Backlog Table' },
      { id: 'bl-create', label: 'Creating Epics & Stories' },
      { id: 'bl-filter', label: 'Filtering & Search' },
      { id: 'bl-sort', label: 'Sorting' },
      { id: 'bl-columns', label: 'Column Visibility' },
      { id: 'bl-drag', label: 'Drag & Drop Priority' },
    ],
  },
  {
    id: 'app-settings', label: 'App Settings',
    subSections: [
      { id: 'as-demo', label: 'Demo & Testing' },
      { id: 'as-export', label: 'Export & Import' },
      { id: 'as-theme', label: 'Dark Mode' },
    ],
  },
];

const DemoImage: React.FC<{ src: string; alt: string; caption?: string }> = ({ src, alt, caption }) => {
  const theme = useTheme();
  return (
    <Box sx={{ my: 3 }}>
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{
          width: '100%',
          maxWidth: 900,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: 3,
          display: 'block',
        }}
      />
      {caption && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
          {caption}
        </Typography>
      )}
    </Box>
  );
};

const SectionHeading: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => (
  <Typography id={id} variant="h5" fontWeight={700} gutterBottom sx={{ mt: 5, scrollMarginTop: '80px' }}>
    {children}
  </Typography>
);

const SubHeading: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => (
  <Typography id={id} variant="h6" fontWeight={600} gutterBottom sx={{ mt: 3, scrollMarginTop: '80px' }}>
    {children}
  </Typography>
);

const Para: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
    {children}
  </Typography>
);

// Static list of all heading IDs derived from the sections constant above
const allIds = sections.flatMap((s) => [s.id, ...(s.subSections?.map((sub) => sub.id) ?? [])]);

const Documentation: React.FC = () => {
  const [activeId, setActiveId] = useState<string>('overview');
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    observerRef.current?.disconnect();
    const callback: IntersectionObserverCallback = (entries) => {
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible.length > 0) setActiveId(visible[0].target.id);
    };
    observerRef.current = new IntersectionObserver(callback, {
      rootMargin: '-64px 0px -60% 0px',
      threshold: 0,
    });
    allIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observerRef.current!.observe(el);
    });
    return () => observerRef.current?.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
      {/* ── Main content ── */}
      <Box sx={{ flex: 1, minWidth: 0 }}>

        {/* ── Overview ── */}
        <SectionHeading id="overview">Overview</SectionHeading>
        <Para>
          <strong>Team Capacity Planner</strong> is a tool designed to help engineering teams plan and
          visualize how their work fits into sprint cycles. It lets you define <em>Pods</em> (teams),
          break down work into <em>Epics</em> and <em>Stories</em>, and then see a visual
          <em> Workstream Forecast</em> that shows when each epic will be completed based on your team's
          velocity.
        </Para>
        <Para>
          The app has four main sections — <strong>Pod Management</strong>, <strong>Epic Planner</strong>,
          <strong>Workstream Forecaster</strong>, and <strong>Backlog</strong>. Start by setting up pods and
          epics, then use the Workstream Forecaster to visualize delivery timelines and the Backlog to
          manage and prioritize your epics in one place.
        </Para>
        <Alert severity="info" sx={{ mb: 3 }}>
          All data is stored in your browser's memory. Refreshing the page will reset it unless you export your
          configuration first. See the <Link onClick={() => scrollTo('as-export')} sx={{ cursor: 'pointer' }}>Export &amp; Import</Link> section for details.
        </Alert>

        <Divider sx={{ my: 4 }} />

        {/* ── Getting Started ── */}
        <SectionHeading id="getting-started">Getting Started</SectionHeading>

        <SubHeading id="gs-navigation">Navigating the App</SubHeading>
        <Para>
          Click the <strong>hamburger menu (☰)</strong> in the top-left corner to open the navigation
          drawer. From here you can jump between Pod Management, Epic Planner, Workstream Forecaster,
          Backlog, and Documentation.
        </Para>
        <DemoImage
          src={imgNavMenu}
          alt="Navigation drawer showing all sections"
          caption="The navigation drawer lists all sections of the app. Click any item to navigate instantly."
        />

        <SubHeading id="gs-demo">Loading Demo Data</SubHeading>
        <Para>
          The quickest way to explore the app is to load the built-in demo configuration. Click the
          <strong> ⚙ Settings</strong> icon in the top-right corner, then click
          <strong> Load Demo Configuration</strong>. This fills the app with example pods, team members,
          epics, and sprint settings so you can see everything in action right away.
        </Para>
        <Para>
          Once you are ready to start fresh, click <strong>Clear Configuration</strong> in the same settings
          panel to reset everything back to an empty state.
        </Para>

        <Divider sx={{ my: 4 }} />

        {/* ── Pod Management ── */}
        <SectionHeading id="pod-management">Pod Management</SectionHeading>
        <Para>
          A <strong>Pod</strong> is a team that works on a set of epics. Each pod has a name, an optional
          description, a list of team members, and a velocity — the number of story points it can deliver
          per sprint.
        </Para>
        <DemoImage
          src={imgPodManagement}
          alt="Pod Management page showing three pods in grid view"
          caption="Pod Management in grid view. Each card shows the pod's name, engineering velocity, and its team members."
        />

        <SubHeading id="pm-create">Creating a Pod</SubHeading>
        <Para>
          Click <strong>+ New Pod</strong> at the top of the Pod Management page. Fill in the pod name
          (required) and an optional description. You can also set the velocity fields — more on those below.
          Click <strong>Save</strong> when done.
        </Para>
        <DemoImage
          src={imgPodForm}
          alt="Edit Pod form showing name, description, and velocity fields"
          caption="The pod form lets you name the pod, describe it, and configure velocity."
        />

        <SubHeading id="pm-velocity">Understanding Velocity</SubHeading>
        <Para>
          Velocity is how many engineering story points a pod can complete in a single sprint. There are two
          ways to set it:
        </Para>
        <List dense>
          <ListItem>
            <ListItemText
              primary={<><strong>Per-engineer average</strong> — enter how many points the average engineer delivers per sprint. The app multiplies this by the number of engineers in the pod to get a calculated velocity.</>}
              secondary={<>Example: 4 engineers × 5 pts each = <Chip label="20 pts" size="small" /> calculated velocity.</>}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary={<><strong>Manual override</strong> — enter a number directly in the "Engineering Velocity" field. When this differs from the calculated value, the chip turns orange to signal an override is active.</>}
              secondary={<>Example: calculated is 20 pts but the field is set to <Chip label="18 pts" size="small" color="warning" variant="outlined" />.</>}
            />
          </ListItem>
        </List>
        <Para>
          Tip: if you leave the per-engineer field at 0, the manual override field is used as-is.
        </Para>

        <SubHeading id="pm-members">Adding Team Members</SubHeading>
        <Para>
          Click the <strong>+ button</strong> on a pod card to open the Add Team Member dialog. Give the
          member a name, choose their <em>Group</em> (Engineering, Design, or Product), and select their
          <em> Practice</em> (e.g. Frontend, Backend, UX Design). Members are colour-coded by group — green
          for Engineering, purple for Design, and blue for Product — making it easy to see team composition
          at a glance.
        </Para>
        <Para>
          Only members in the <strong>Engineering</strong> group count toward the per-engineer velocity
          calculation.
        </Para>

        <SubHeading id="pm-drag">Drag &amp; Drop</SubHeading>
        <Para>
          You can reorder team members within a pod, or move them between pods, by dragging the
          <strong> ⠿ drag handle</strong> on the left side of each member row. There is also a special
          <em> Unassigned</em> area at the bottom — drag members there to park them without removing them
          from the system.
        </Para>

        <Divider sx={{ my: 4 }} />

        {/* ── Epic Planner ── */}
        <SectionHeading id="epic-planner">Epic Planner</SectionHeading>
        <Para>
          An <strong>Epic</strong> is a large piece of work assigned to a pod. Each epic has a title,
          description, a category (for colour-coding), and a breakdown of engineering, product, and design
          story points. The Workstream Forecaster uses these story points to schedule when the work will
          be delivered.
        </Para>
        <DemoImage
          src={imgEpicPlanner}
          alt="Epic Planner page showing epics for each pod"
          caption="Epic Planner in grid view. Each column is a pod; each row is an epic with its Eng/Prod/Design point totals."
        />

        <SubHeading id="ep-create">Creating an Epic</SubHeading>
        <Para>
          Click <strong>+ New Epic</strong>. Give it a title, description, choose which pod it belongs to,
          and optionally assign a category. Epics are ordered by priority within each pod — you can drag
          them to reorder on both the Epic Planner and Workstream Forecaster pages.
        </Para>

        <SubHeading id="ep-categories">Categories</SubHeading>
        <Para>
          Categories are labels with a colour that you create yourself (e.g. "Feature", "Infrastructure",
          "UX Improvement"). They appear as a coloured left border on each epic card and as colour blocks in
          the Workstream Forecaster burndown chart. To add a new category, click <strong>Add Category</strong>
          inside the epic form, give it a name, and pick a colour.
        </Para>

        <SubHeading id="ep-stories">Stories &amp; Story Points</SubHeading>
        <Para>
          Inside each epic you can add individual <strong>Stories</strong> — small, estimable units of work.
          Each story has:
        </Para>
        <List dense>
          <ListItem><ListItemText primary={<><strong>Type</strong> — Engineering, Product, or Design.</>} /></ListItem>
          <ListItem><ListItemText primary={<><strong>Sub-type</strong> (Engineering only) — Frontend, Backend, Full Stack, Data Engineering, or DevOps.</>} /></ListItem>
          <ListItem><ListItemText primary={<><strong>Title</strong> — a short description of the work.</>} /></ListItem>
          <ListItem><ListItemText primary={<><strong>Points</strong> — the estimated story points for this story.</>} /></ListItem>
        </List>
        <Para>
          The <strong>Story Point Totals</strong> section at the bottom of the epic form automatically sums
          up all story points by type. These totals are what the Workstream Forecaster uses.
        </Para>
        <DemoImage
          src={imgEpicForm}
          alt="Epic form showing story breakdown and point totals"
          caption="The epic form with a full story breakdown. Point totals are computed automatically from individual stories."
        />

        <SubHeading id="ep-overrides">Manual Overrides</SubHeading>
        <Para>
          If you don't want to break an epic down into individual stories but still need a point estimate,
          you can type directly into the <strong>Story Point Totals</strong> fields. The override is
          highlighted with an orange "outlined" chip on the Epic Planner to remind you that no backing
          stories exist. To switch back to story-driven totals, add at least one story of that type.
        </Para>

        <Divider sx={{ my: 4 }} />

        {/* ── Workstream Forecaster ── */}
        <SectionHeading id="workstream-forecaster">Workstream Forecaster</SectionHeading>
        <Para>
          The <strong>Workstream Forecaster</strong> is a visual sprint timeline that shows how your epics
          will be delivered across sprints. Each pod gets its own row, and each sprint is a card showing
          the velocity and a colour-coded burndown bar.
        </Para>
        <DemoImage
          src={imgWorkstreamForecaster}
          alt="Workstream Forecaster showing sprint burndown for all pods"
          caption="Workstream Forecaster. Month headers span the top; each pod shows sprint cards with coloured burn bars."
        />

        <SubHeading id="wf-timeline">Timeline &amp; Sprints</SubHeading>
        <Para>
          Click the <strong>⚙ Configure Timeline</strong> icon (top-left of the forecaster) to set:
        </Para>
        <List dense>
          <ListItem><ListItemText primary={<><strong>Start Date</strong> — the first day of Sprint 1. Defaults to the next Monday.</>} /></ListItem>
          <ListItem><ListItemText primary={<><strong>Total Sprints</strong> — how many sprints to show (default: 8, up to any number you need).</>} /></ListItem>
        </List>
        <Para>
          Sprint length is configured globally in the settings panel (⚙ Settings → Sprint length in days).
          By default each sprint is 10 business days (2 weeks).
        </Para>

        <SubHeading id="wf-burndown">Epic Burn Visualisation</SubHeading>
        <Para>
          Each sprint card contains a coloured bar that shows how the pod's velocity is consumed by epics
          in that sprint. Here is how it works:
        </Para>
        <List dense>
          <ListItem>
            <ListItemText primary={<>Epics are worked through in <strong>priority order</strong> (top to bottom as they appear in the Epic Planner).</>} />
          </ListItem>
          <ListItem>
            <ListItemText
              primary={<>In each sprint, the full velocity is allocated to the highest-priority epic first. When that epic is finished, remaining capacity flows to the next epic.</>}
              secondary={<>Example: Pod velocity = 20 pts, Epic A = 14 pts remaining, Epic B = 30 pts. Sprint burns 14 pts on A (70%) and 6 pts on B (30%).</>}
            />
          </ListItem>
          <ListItem>
            <ListItemText primary={<>Each epic's colour comes from its <strong>Category</strong>. Grey means no category is assigned.</>} />
          </ListItem>
        </List>
        <Para>
          Click <strong>Show Epics</strong> (▼ toggle) on any pod row to expand a colour-coded legend
          beneath the sprint cards. You can also drag epics in the legend to change their priority, and the
          burndown updates instantly.
        </Para>
        <Para>
          Hovering over a coloured bar segment highlights that epic across <em>all</em> sprint cards so you
          can trace its full journey through the timeline.
        </Para>

        <SubHeading id="wf-overrides">Sprint Overrides</SubHeading>
        <Para>
          Click the <strong>✎ pencil icon</strong> on any sprint card to set a custom velocity for that
          specific sprint and pod. This is useful for sprints with holidays, reduced headcount, or other
          factors that affect capacity. Overridden sprints show their velocity chip in orange.
        </Para>
        <Para>
          Example: your pod normally delivers 20 pts per sprint, but Sprint 4 has a bank holiday so capacity
          drops. Set an override of 12 pts for that sprint only — all other sprints keep the default.
        </Para>

        <SubHeading id="wf-attention">Attention Warning</SubHeading>
        <Para>
          If a pod's total epic story points exceed the combined capacity of all displayed sprints, an
          <Chip label="Attention" size="small" variant="outlined" color="warning" sx={{ mx: 0.5 }} />
          badge appears next to the pod name. This is a signal to either increase the number of sprints,
          reduce scope, or adjust velocity. It does not block anything — it is just a heads-up.
        </Para>

        <Divider sx={{ my: 4 }} />

        {/* ── Backlog ── */}
        <SectionHeading id="backlog">Backlog</SectionHeading>
        <Para>
          The <strong>Backlog</strong> is a flat, prioritized list of all your epics across every pod.
          It gives you a single table view where you can search, filter, sort, reorder, and inline-edit
          epics without switching between pod columns. Changes made here — especially drag-and-drop
          priority reordering — are reflected instantly in the Workstream Forecaster.
        </Para>
        <DemoImage
          src={imgBacklog}
          alt="Backlog page showing all epics in a table with pod, category, points, and type columns"
          caption="Backlog table view. Every epic in the system appears here with its pod, category, point totals, and story count."
        />

        <SubHeading id="bl-table">Backlog Table</SubHeading>
        <Para>
          Each row in the table represents one epic. The default columns shown are:
        </Para>
        <List dense>
          <ListItem><ListItemText primary={<><strong>Title</strong> — the epic name. Bold rows are epics; indented rows are their stories (visible when expanded).</>} /></ListItem>
          <ListItem><ListItemText primary={<><strong>Pod</strong> — which pod owns this epic.</>} /></ListItem>
          <ListItem><ListItemText primary={<><strong>Category</strong> — the colour-coded category chip assigned to the epic.</>} /></ListItem>
          <ListItem><ListItemText primary={<><strong>Points</strong> — total engineering story points. An outlined orange chip means a manual override is active with no backing stories.</>} /></ListItem>
          <ListItem><ListItemText primary={<><strong>Stories</strong> — the number of individual stories inside the epic.</>} /></ListItem>
          <ListItem><ListItemText primary={<><strong>Type</strong> — always "Epic" for top-level rows; stories show their engineering sub-type (e.g. Backend, Frontend).</>} /></ListItem>
        </List>
        <Para>
          Click the <strong>▶ expand arrow</strong> on any epic row to reveal its child stories
          inline. Each story row shows its own title, points, and sub-type. Click again to collapse.
        </Para>
        <DemoImage
          src={imgBacklogExpanded}
          alt="Backlog with first epic expanded showing its child stories"
          caption="Expanding an epic row reveals its individual stories with title, points, and engineering sub-type."
        />

        <SubHeading id="bl-create">Creating Epics &amp; Stories</SubHeading>
        <Para>
          Use the <strong>+ Create</strong> button at the top-left to quickly create a new epic. The
          split-arrow dropdown beside it lets you choose whether to create an <em>Epic</em> or a
          <em> Story</em> directly (the story creation dialog lets you pick which epic to attach it to).
        </Para>
        <Para>
          You can also open an existing epic's full edit form by clicking the <strong>✎ edit icon</strong>
          that appears on the right side of each epic row. This opens the same detailed form as the
          Epic Planner, where you can update titles, descriptions, categories, pod assignments, and
          individual stories.
        </Para>

        <SubHeading id="bl-filter">Filtering &amp; Search</SubHeading>
        <Para>
          Two tools help you narrow down what you see:
        </Para>
        <List dense>
          <ListItem>
            <ListItemText
              primary={<><strong>Search bar</strong> — type any text to instantly filter epics by title or description. Matching epics remain visible; non-matching rows are hidden.</>}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary={<><strong>Pod filter</strong> — click the <Chip label="Pods ▼" size="small" /> button to open the pod picker. Select one or more pods to show only their epics. Active pod filters appear as removable chips next to the button.</>}
            />
          </ListItem>
        </List>
        <Para>
          Both filters work together — the table shows only epics that match both the search text and
          the selected pods simultaneously.
        </Para>

        <SubHeading id="bl-sort">Sorting</SubHeading>
        <Para>
          Click any column header to sort the table by that column. Click again to reverse the sort
          direction (ascending → descending → cleared). A small arrow indicator appears in the active
          sort header. Sorting is independent of drag-and-drop priority — once you clear the sort, epics
          return to their drag-ordered priority sequence.
        </Para>

        <SubHeading id="bl-columns">Column Visibility</SubHeading>
        <Para>
          Click the <strong>⚙ configure columns</strong> icon (top-right of the toolbar) to toggle which
          columns are visible. You can show or hide any of the six available columns: Pod, Category,
          Points, Stories, Type, and Priority. The Priority column is hidden by default — enable it to
          see each epic's numeric priority rank alongside its drag-order position.
        </Para>

        <SubHeading id="bl-drag">Drag &amp; Drop Priority</SubHeading>
        <Para>
          Grab the <strong>⠿ drag handle</strong> on the far-left of any epic row to reorder it within
          the backlog. Dragging an epic higher in the list increases its priority; dragging it lower
          decreases it. This priority order is the same one the Workstream Forecaster uses when
          scheduling work into sprints — higher-priority epics are scheduled first and receive capacity
          before lower-priority ones.
        </Para>
        <Alert severity="info" sx={{ mb: 3 }}>
          Drag-and-drop reordering is only available when <strong>no sort column is active</strong>.
          Clear any active sort first to restore the draggable order.
        </Alert>

        <Divider sx={{ my: 4 }} />

        {/* ── App Settings ── */}
        <SectionHeading id="app-settings">App Settings</SectionHeading>
        <Para>
          Click the <strong>⚙ Settings icon</strong> in the top-right corner of the app bar to open the
          settings panel.
        </Para>

        <SubHeading id="as-demo">Demo &amp; Testing</SubHeading>
        <Para>
          <strong>Load Demo Configuration</strong> — fills the app with realistic sample data (three pods,
          ~30 epics across different categories) so you can explore the full feature set without setting
          anything up manually.
        </Para>
        <Para>
          <strong>Clear Configuration</strong> — removes all pods, epics, and sprint overrides and resets
          the timeline to start from the next Monday. Use this when you are ready to work with your real
          data.
        </Para>

        <SubHeading id="as-export">Export &amp; Import</SubHeading>
        <Para>
          Since data lives only in browser memory, it is lost on a page refresh. To save your work:
        </Para>
        <List dense>
          <ListItem>
            <ListItemText
              primary={<><strong>Export Epic &amp; Story Details</strong> — downloads a <code>epic-configuration.json</code> file containing all your epics, stories, and categories.</>}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary={<><strong>Import Epic Configuration</strong> — upload a previously exported JSON file to restore your epics and categories. Pod and sprint settings are not included in the export.</>}
            />
          </ListItem>
        </List>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Importing a configuration replaces your current epics and categories. Make sure to export first if
          you want to keep your existing data.
        </Alert>

        <SubHeading id="as-theme">Dark Mode</SubHeading>
        <Para>
          Toggle the <strong>Light Mode / Dark Mode</strong> switch in the settings panel to switch the
          colour theme. Your preference is remembered within the current session.
        </Para>

        <Divider sx={{ my: 4 }} />
        <Typography variant="body2" color="text.secondary" sx={{ pb: 4 }}>
          That covers all the core features of Team Capacity Planner. If something is unclear, try loading
          the demo data — it is the best way to see everything working together.
        </Typography>
      </Box>

      {/* ── Right-side navigation ── */}
      <Box
        component="nav"
        aria-label="Documentation sections"
        sx={{
          width: 220,
          flexShrink: 0,
          display: { xs: 'none', md: 'block' },
          position: 'sticky',
          top: 80,
          alignSelf: 'flex-start',
          maxHeight: 'calc(100vh - 100px)',
          overflowY: 'auto',
        }}
      >
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="overline" color="text.secondary" fontWeight={600}>
            On this page
          </Typography>
          <List dense disablePadding sx={{ mt: 1 }}>
            {sections.map((section) => (
              <React.Fragment key={section.id}>
                <ListItem disablePadding>
                  <Box
                    component="button"
                    onClick={() => scrollTo(section.id)}
                    sx={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      width: '100%',
                      py: 0.4,
                      px: 0,
                      fontWeight: activeId === section.id ? 700 : 400,
                      color: activeId === section.id ? 'primary.main' : 'text.primary',
                      fontSize: '0.85rem',
                      fontFamily: 'inherit',
                      borderLeft: activeId === section.id ? `3px solid` : '3px solid transparent',
                      borderColor: activeId === section.id ? 'primary.main' : 'transparent',
                      pl: activeId === section.id ? 1 : 0,
                      transition: 'all 0.15s',
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    {section.label}
                  </Box>
                </ListItem>
                {section.subSections?.map((sub) => (
                  <ListItem key={sub.id} disablePadding>
                    <Box
                      component="button"
                      onClick={() => scrollTo(sub.id)}
                      sx={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                        width: '100%',
                        py: 0.3,
                        pl: activeId === sub.id ? 2.5 : 1.5,
                        fontWeight: activeId === sub.id ? 600 : 400,
                        color: activeId === sub.id ? 'primary.main' : 'text.secondary',
                        fontSize: '0.78rem',
                        fontFamily: 'inherit',
                        borderLeft: activeId === sub.id ? '3px solid' : '3px solid transparent',
                        borderColor: activeId === sub.id ? 'primary.main' : 'transparent',
                        transition: 'all 0.15s',
                        '&:hover': { color: 'primary.main' },
                      }}
                    >
                      {sub.label}
                    </Box>
                  </ListItem>
                ))}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      </Box>
    </Box>
  );
};

export default Documentation;
